import { Dispatch } from "redux";

import {
  CONTENT_WORD_START_PLAY,
  CONTENT_WORD_IMPORT,
  CONTENT_WORD_COUNTDOWN,
  CONTENT_WORD_SHUFFLE,
  CONTENT_WORD_SELECT_ROUND,
  CONTENT_WORD_SET_DISPLAY,
  CONTENT_WORD_SET_ANSWER,
  CONTENT_WORD_INIT_GAME,
  CONTENT_WORD_SELECT_ANSWER,
  CONTENT_WORD_TIMES_UP,
  CONTENT_WORD_END_RESPONSE,
  CONTENT_WORD_PLAY_AGAIN,
} from "./actionIdentifiers";
import { State } from "../store";
import { random, timeout, explodeArray, findJsonValue } from "../../util/Util";
import { srcPath, get } from "../../util/Path";
import { gameConfigLoad } from "./shared";

export const contentWordAudio = {
  countdown: new Audio(srcPath("content-word/sounds/countdown.wav")),
  correct: new Audio(srcPath("shared/clapping.mp3")),
  wrong: new Audio(srcPath("content-word/sounds/wrong.wav")),
  timesUp: new Audio(),
};

export const importWords = (grade: number, unit: number) => {
  return async (dispatch: Dispatch<any>, getState: () => State) => {
    let words: any[];

    const res = await get("config/content-word.json");
    const contentWordList = res.data;

    contentWordList.some((cw) => {
      if (cw.grade !== grade) return false;

      return cw.units.some((u) => {
        if (u.unit === unit && u.words.length > 0) {
          words = u.words;
          return true;
        }
        return false;
      });
    });

    if (!words || !words.length) {
      dispatch({
        type: CONTENT_WORD_IMPORT,
        payload: { words: [], specialWords: null },
      });
    } else {
      const specialWords = findJsonValue(contentWordList, "special_words", {
        grade,
        unit,
      });

      dispatch({
        type: CONTENT_WORD_IMPORT,
        payload: { words: [...new Set(explodeArray(words))], specialWords },
      });
    }

    dispatch(gameConfigLoad());
  };
};

export const startPlay = () => ({
  type: CONTENT_WORD_START_PLAY,
});

export const initGame = () => {
  return async (dispatch: Dispatch<any>, getState: () => State) => {
    const { grade, unit } = getState().shared;

    const res = await get("config/content-word.json");
    const contentWordList = res.data;

    const score = 0;
    const time = findJsonValue(contentWordList, "time", { grade, unit });
    const score_per_question = findJsonValue(
      contentWordList,
      "score_per_question",
      { grade, unit }
    );
    const incorrect_reduce_score = findJsonValue(
      contentWordList,
      "incorrect_reduce_score",
      { grade, unit }
    );

    dispatch({
      type: CONTENT_WORD_INIT_GAME,
      payload: { score, time, score_per_question, incorrect_reduce_score },
    });
  };
};

export const selectRound = (round: number) => ({
  type: CONTENT_WORD_SELECT_ROUND,
  payload: round,
});

export const shuffleWords = () => {
  return (dispatch: Dispatch<any>, getState: () => State) => {
    const words = [...getState().contentWord.words];
    words.sort(() => Math.random() - 0.5);
    dispatch({ type: CONTENT_WORD_SHUFFLE, payload: words });
  };
};

export const countdown = () => {
  return (dispatch: Dispatch<any>, getState: () => State) => {
    let time = getState().contentWord.time;
    dispatch({ type: CONTENT_WORD_COUNTDOWN, payload: time-- });

    const interval = setInterval(() => {
      if (time >= 1) {
        dispatch({ type: CONTENT_WORD_COUNTDOWN, payload: time-- });
      } else {
        contentWordAudio.timesUp.play();
        clearInterval(interval);
        dispatch({ type: CONTENT_WORD_TIMES_UP });
      }
    }, 1000);
  };
};

export const setAnswer = () => {
  return (dispatch: Dispatch<any>, getState: () => State) => {
    const { words, round } = getState().contentWord;

    dispatch({ type: CONTENT_WORD_SET_ANSWER, payload: words[round] });
  };
};

export const setDisplay = () => {
  return (dispatch: Dispatch<any>, getState: () => State) => {
    const { words, round, specialWords } = getState().contentWord;

    const answer = words[round];
    let newArr: string[];

    if (specialWords && specialWords[answer]) {
      const specials = [...specialWords[answer]];
      specials.sort(() => Math.random() - 0.5);
      newArr = [...specials.splice(0, 2), answer];
      newArr.sort(() => Math.random() - 0.5);
    } else {
      const arr = [round];
      for (let i = 0; i < 2; i++) {
        let index: number;
        do {
          index = random(words.length);
        } while (arr.includes(index));
        arr.push(index);
      }
      arr.sort(() => Math.random() - 0.5);
      newArr = arr.map((i) => words[i]);
    }

    dispatch({ type: CONTENT_WORD_SET_DISPLAY, payload: newArr });
  };
};

export const selectAnswer = (selected: number) => {
  return async (dispatch: Dispatch<any>, getState: () => State) => {
    const {
      display,
      answer,
      score_per_question,
      incorrect_reduce_score,
    } = getState().contentWord;

    let score = getState().contentWord.score;
    let response: string;

    if (display[selected] === answer) {
      score += score_per_question;
      response = "correct";
    } else {
      score -= incorrect_reduce_score;
      if (score < 0) score = 0;
      response = "wrong";
    }

    await contentWordAudio[response].play();

    dispatch({
      type: CONTENT_WORD_SELECT_ANSWER,
      payload: { score, selected, response },
    });

    await timeout(1000);
    dispatch({ type: CONTENT_WORD_END_RESPONSE });
  };
};

export const playAgain = () => {
  return { type: CONTENT_WORD_PLAY_AGAIN };
};
